import type { BusinessProfile, Customer } from "@agency/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { callAction } from "../api";
import { useConfig } from "./ConfigProvider";
import { useToast } from "./Toast";
import { Button, Input, Select, Sheet, Textarea, Checkbox } from "./ui";
import { useT } from "../i18n";
import { formatPhone } from "../theme";

const ENTITY_TYPES = ["sole_proprietor", "partnership", "llc", "corporation", "s_corp", "nonprofit", "other"] as const;
const STATUSES = ["prospect", "active", "lapsed", "lost"] as const;

interface Form {
  kind: "person" | "business"; firstName: string; lastName: string; businessName: string;
  email: string; phone: string; phone2: string; dob: string; preferredLocale: "en" | "es"; status: (typeof STATUSES)[number]; ownerUserId: string; tags: string; source: string; whatsapp: boolean;
  line1: string; line2: string; city: string; state: string; zip: string;
  entityType: string; fein: string; naics: string; description: string; yearStarted: string; employeesFullTime: string; employeesPartTime: string; annualRevenue: string; annualPayroll: string; website: string; contactName: string; contactTitle: string;
}

const num = (s: string) => (s.trim() === "" ? undefined : Number(s.replace(/[$,\s]/g, "")));
const dollarsToCents = (s: string) => { const n = num(s); return n == null || isNaN(n) ? undefined : Math.round(n * 100); };

function fromCustomer(c: Customer): Form {
  const b = c.business ?? {};
  return {
    kind: c.kind, firstName: c.firstName, lastName: c.lastName, businessName: c.businessName ?? "",
    email: c.email ?? "", phone: formatPhone(c.phones[0]?.number) ?? "", phone2: formatPhone(c.phones[1]?.number) ?? "", dob: c.dob ?? "",
    preferredLocale: c.preferredLocale, status: c.status, ownerUserId: c.ownerUserId ?? "", tags: c.tags.join(", "), source: c.source ?? "", whatsapp: !!c.phones[0]?.whatsapp,
    line1: c.address?.line1 ?? "", line2: c.address?.line2 ?? "", city: c.address?.city ?? "", state: c.address?.state ?? "TX", zip: c.address?.zip ?? "",
    entityType: b.entityType ?? "", fein: b.fein ?? "", naics: b.naics ?? "", description: b.description ?? "", yearStarted: b.yearStarted?.toString() ?? "",
    employeesFullTime: b.employeesFullTime?.toString() ?? "", employeesPartTime: b.employeesPartTime?.toString() ?? "",
    annualRevenue: b.annualRevenueCents != null ? (b.annualRevenueCents / 100).toFixed(0) : "", annualPayroll: b.annualPayrollCents != null ? (b.annualPayrollCents / 100).toFixed(0) : "",
    website: b.website ?? "", contactName: b.contactName ?? "", contactTitle: b.contactTitle ?? "",
  };
}

/** Edit a customer: identity, business profile, contact, address, status, owner, tags. Saves through agency.customer.update with the version it was opened at. */
export function CustomerEditSheet({ open, onClose, customer, onSaved }: { open: boolean; onClose: () => void; customer: Customer; onSaved?: () => void }) {
  const t = useT();
  const { config } = useConfig();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<Form>(() => fromCustomer(customer));
  useEffect(() => { if (open) setForm(fromCustomer(customer)); }, [open, customer]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const m = useMutation({
    mutationFn: () => {
      const addressFilled = form.line1.trim() || form.city.trim() || form.zip.trim();
      const business: BusinessProfile | undefined = form.kind === "business" ? {
        legalName: form.businessName.trim() || undefined, entityType: (form.entityType || undefined) as BusinessProfile["entityType"], fein: form.fein.trim() || undefined, naics: form.naics.trim() || undefined,
        description: form.description.trim() || undefined, yearStarted: num(form.yearStarted), employeesFullTime: num(form.employeesFullTime), employeesPartTime: num(form.employeesPartTime),
        annualRevenueCents: dollarsToCents(form.annualRevenue), annualPayrollCents: dollarsToCents(form.annualPayroll), website: form.website.trim() || undefined, contactName: form.contactName.trim() || undefined, contactTitle: form.contactTitle.trim() || undefined,
      } : undefined;
      return callAction<"agency.customer.update", Customer>("agency.customer.update", {
        customerId: customer.id, expectedVersion: customer.version,
        patch: {
          kind: form.kind, firstName: form.firstName.trim(), lastName: form.lastName.trim(),
          businessName: form.kind === "business" ? form.businessName.trim() : null,
          ...(business ? { business } : {}),
          email: form.email.trim() || null, phone: form.phone.trim() || null, phone2: form.phone2.trim() || null, dob: form.dob || null,
          preferredLocale: form.preferredLocale, status: form.status, ownerUserId: form.ownerUserId || null,
          tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
          source: form.source.trim() || null, whatsapp: form.whatsapp,
          address: addressFilled ? { line1: form.line1.trim(), line2: form.line2.trim() || undefined, city: form.city.trim(), state: form.state.trim().toUpperCase().slice(0, 2) || "TX", zip: form.zip.trim() } : null,
        },
      });
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["customer", customer.id] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["search"] });
      qc.invalidateQueries({ queryKey: ["today"] });
      qc.invalidateQueries({ queryKey: ["pipeline"] });
      toast({ title: r.result.version === customer.version ? t("customers.noChanges") : t("customers.saved"), tone: "success" });
      onSaved?.();
      onClose();
    },
    onError: (e) => {
      const err = e as Error & { status?: number };
      toast({ title: t("common.error"), body: err.status === 409 && /version/i.test(err.message) ? t("customers.versionConflict") : err.message, tone: "error" });
    },
  });

  const submit = (e: FormEvent) => { e.preventDefault(); m.mutate(); };
  const field = "grid grid-cols-2 gap-3";

  return (
    <Sheet open={open} onClose={onClose} title={t("customers.editTitle")}>
      <form onSubmit={submit} className="space-y-4">
        <Select label={t("customers.kind")} value={form.kind} onChange={(e) => set("kind", e.target.value as Form["kind"])}>
          <option value="person">{t("customers.kind.person")}</option>
          <option value="business">{t("customers.kind.business")}</option>
        </Select>
        {form.kind === "business" ? <Input label={t("customers.businessName")} required value={form.businessName} onChange={(e) => set("businessName", e.target.value)} /> : null}
        <div className={field}>
          <Input label={t("customers.firstName")} required value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
          <Input label={t("customers.lastName")} required value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
        </div>

        {form.kind === "business" ? (
          <fieldset className="rounded-2xl border border-ink/10 p-3 space-y-3">
            <legend className="px-1 text-sm font-bold">{t("customers.business.title")}</legend>
            <p className="text-xs text-ink/55">{t("customers.business.help")}</p>
            <Select label={t("customers.business.entityType")} value={form.entityType} onChange={(e) => set("entityType", e.target.value)}>
              <option value="">—</option>
              {ENTITY_TYPES.map((k) => <option key={k} value={k}>{t(`customers.business.entity.${k}`)}</option>)}
            </Select>
            <div>
              <Input label={t("customers.business.fein")} value={form.fein} onChange={(e) => set("fein", e.target.value)} placeholder="12-3456789" />
              <p className="mt-1 text-xs text-ink/50">{t("customers.business.feinHelp")}</p>
            </div>
            <div className={field}>
              <Input label={t("customers.business.naics")} value={form.naics} onChange={(e) => set("naics", e.target.value)} />
              <Input label={t("customers.business.yearStarted")} inputMode="numeric" value={form.yearStarted} onChange={(e) => set("yearStarted", e.target.value)} />
            </div>
            <Textarea label={t("customers.business.description")} value={form.description} onChange={(e) => set("description", e.target.value)} className="min-h-[72px]" />
            <div className={field}>
              <Input label={t("customers.business.employeesFullTime")} inputMode="numeric" value={form.employeesFullTime} onChange={(e) => set("employeesFullTime", e.target.value)} />
              <Input label={t("customers.business.employeesPartTime")} inputMode="numeric" value={form.employeesPartTime} onChange={(e) => set("employeesPartTime", e.target.value)} />
            </div>
            <div className={field}>
              <Input label={t("customers.business.annualRevenue")} inputMode="decimal" value={form.annualRevenue} onChange={(e) => set("annualRevenue", e.target.value)} />
              <Input label={t("customers.business.annualPayroll")} inputMode="decimal" value={form.annualPayroll} onChange={(e) => set("annualPayroll", e.target.value)} />
            </div>
            <Input label={t("customers.business.website")} value={form.website} onChange={(e) => set("website", e.target.value)} />
            <div className={field}>
              <Input label={t("customers.business.contactName")} value={form.contactName} onChange={(e) => set("contactName", e.target.value)} />
              <Input label={t("customers.business.contactTitle")} value={form.contactTitle} onChange={(e) => set("contactTitle", e.target.value)} />
            </div>
          </fieldset>
        ) : null}

        <fieldset className="rounded-2xl border border-ink/10 p-3 space-y-3">
          <legend className="px-1 text-sm font-bold">{t("customers.contactSection")}</legend>
          <Input label={t("customers.email")} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          <div className={field}>
            <Input label={t("common.phone")} type="tel" inputMode="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(915) 555-0100" />
            <Input label={t("customers.phone2")} type="tel" inputMode="tel" value={form.phone2} onChange={(e) => set("phone2", e.target.value)} />
          </div>
          <Input label={t("customers.address.line1")} value={form.line1} onChange={(e) => set("line1", e.target.value)} />
          <Input label={t("customers.address.line2")} value={form.line2} onChange={(e) => set("line2", e.target.value)} />
          <div className="grid grid-cols-[1fr_72px_110px] gap-3">
            <Input label={t("customers.address.city")} value={form.city} onChange={(e) => set("city", e.target.value)} />
            <Input label={t("customers.address.state")} maxLength={2} value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase())} />
            <Input label={t("customers.address.zip")} inputMode="numeric" value={form.zip} onChange={(e) => set("zip", e.target.value)} />
          </div>
          {form.kind === "person" ? <Input label={t("customers.dob")} type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} /> : null}
          <Select label={t("customers.preferredLanguage")} value={form.preferredLocale} onChange={(e) => set("preferredLocale", e.target.value as "en" | "es")}>
            <option value="en">{t("common.english")}</option>
            <option value="es">{t("common.spanish")}</option>
          </Select>
        </fieldset>

        <div className={field}>
          <Select label={t("customers.status")} value={form.status} onChange={(e) => set("status", e.target.value as Form["status"])}>
            {STATUSES.map((s) => <option key={s} value={s}>{t(`customers.status.${s}`)}</option>)}
          </Select>
          <Select label={t("common.owner")} value={form.ownerUserId} onChange={(e) => set("ownerUserId", e.target.value)}>
            <option value="">{t("customers.ownerNone")}</option>
            {config.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
        </div>
        <div>
          <Input label={t("customers.tags")} value={form.tags} onChange={(e) => set("tags", e.target.value)} />
          <p className="mt-1 text-xs text-ink/50">{t("customers.tagsHelp")}</p>
          <Input label={t("extras.leadSource")} list="lead-sources" value={form.source} onChange={(e) => set("source", e.target.value)} />
          <datalist id="lead-sources">{["walk-in", "referral", "website", "google", "facebook", "phone", "carrier", "other"].map((s) => <option key={s} value={s} />)}</datalist>
          <Checkbox label={t("extras.whatsapp")} checked={form.whatsapp} onChange={(v: boolean) => set("whatsapp", v)} />
        </div>
        <Button type="submit" size="lg" className="w-full" loading={m.isPending}>{t("common.save")}</Button>
      </form>
    </Sheet>
  );
}
